import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download, Send, Loader2, MessageCircle, FileBox } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColor = (s) => ({
  quote_requested: 'bg-amber-50 text-amber-700 border-amber-200',
  quoted:          'bg-blue-50 text-blue-700 border-blue-200',
  approved:        'bg-emerald-50 text-emerald-700 border-emerald-200',
  in_production:   'bg-orange-50 text-orange-700 border-orange-200',
  shipped:         'bg-indigo-50 text-indigo-700 border-indigo-200',
  completed:       'bg-zinc-100 text-zinc-700 border-zinc-200',
  cancelled:       'bg-rose-50 text-rose-700 border-rose-200'
}[s] || 'bg-zinc-100 text-zinc-700 border-zinc-200');

const OrderDetail = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const loadOrder = async () => {
    const { data } = await axios.get(`${API}/orders/${orderNumber}`);
    setOrder(data);
  };
  const loadMsgs = async () => {
    const { data } = await axios.get(`${API}/orders/${orderNumber}/messages`);
    setMsgs(data);
  };

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([loadOrder(), loadMsgs()]);
      } catch (e) { console.error(e); }
    })();
  }, [orderNumber]); // eslint-disable-line

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [msgs.length]);

  const sendMsg = async () => {
    const txt = text.trim();
    if (!txt) return;
    setSending(true);
    try {
      await axios.post(`${API}/orders/${orderNumber}/messages`, { text: txt });
      setText('');
      await loadMsgs();
    } catch (e) {
      console.error(e);
      toast({ title: 'Hata', description: 'Gönderilemedi' });
    } finally {
      setSending(false);
    }
  };

  const downloadFile = async () => {
    try {
      const res = await axios.get(`${API}/orders/${orderNumber}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = order.file_name || 'model.stl';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast({ title: 'Hata', description: 'İndirilemedi' });
    }
  };

  if (!order) {
    return (
      <div className="max-w-5xl mx-auto px-5 py-12 bg-white">
        <div className="text-zinc-500">…</div>
      </div>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      <div className="max-w-5xl mx-auto px-5 lg:px-8 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-orange-500 text-sm mb-5">
          <ArrowLeft className="w-4 h-4" /> {t.dashboard.backToList}
        </Link>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-zinc-900">{order.order_number}</h1>
            <p className="text-zinc-500 text-sm mt-1">{new Date(order.created_at).toLocaleString(lang === 'tr' ? 'tr-TR' : 'en-US')}</p>
          </div>
          <Badge className={`${statusColor(order.status)} border font-medium`}>
            {t.dashboard.statuses[order.status] || order.status}
          </Badge>
        </div>

        {order.quoted_price ? (
          <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200 p-6 mb-6">
            <div className="text-xs uppercase tracking-widest text-orange-700">Teklif Fiyatı</div>
            <div className="text-3xl font-bold text-orange-600 mt-1">{order.quoted_price.toLocaleString('tr-TR')} ₺</div>
          </Card>
        ) : null}

        <div className="grid md:grid-cols-2 gap-5">
          <Card className="bg-white border-zinc-200 p-5">
            <h3 className="text-zinc-900 font-semibold mb-3">{t.dashboard.productionParams}</h3>
            <Row label={t.dashboard.tech} value={(order.technology || '').toUpperCase()} />
            <Row label={t.dashboard.material} value={order.material_label} />
            <Row label="Renk" value={order.color} />
            <Row label="Katman" value={`${order.layer_height} mm`} />
            {order.technology === 'fdm' && <Row label="Dolgu" value={`%${order.infill}`} />}
            <Row label={t.dashboard.qty} value={order.quantity} />
            {order.notes && <Row label="Notlar" value={order.notes} />}
          </Card>

          <Card className="bg-white border-zinc-200 p-5">
            <h3 className="text-zinc-900 font-semibold mb-3">{t.dashboard.file}</h3>
            <div className="flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-200 rounded-lg mb-3">
              <div className="w-10 h-10 rounded-md bg-orange-500/10 border border-orange-500/30 flex items-center justify-center">
                <FileBox className="w-4 h-4 text-orange-500" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-zinc-900 text-sm font-medium truncate">{order.file_name}</div>
                <div className="text-xs text-zinc-500">{((order.file_size_bytes || 0) / 1024 / 1024).toFixed(2)} MB</div>
              </div>
            </div>
            <Button onClick={downloadFile} className="w-full bg-zinc-900 hover:bg-zinc-800 text-white border-0">
              <Download className="w-4 h-4 mr-2" /> {t.dashboard.download}
            </Button>

            <div className="mt-5">
              <h3 className="text-zinc-900 font-semibold mb-2">{t.dashboard.contactInfo}</h3>
              <div className="text-sm text-zinc-600 space-y-1">
                <div>{order.contact_name}</div>
                <div>{order.contact_email}</div>
                {order.contact_phone && <div>{order.contact_phone}</div>}
              </div>
            </div>
          </Card>
        </div>

        {/* Messages */}
        <Card className="bg-white border-zinc-200 mt-5">
          <div className="px-5 py-4 border-b border-zinc-200 flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-orange-500" />
            <h3 className="text-zinc-900 font-semibold">{t.dashboard.messages}</h3>
          </div>
          <div ref={scrollRef} className="px-5 py-5 max-h-80 overflow-y-auto space-y-3">
            {msgs.length === 0 ? (
              <div className="text-zinc-400 text-sm text-center py-6">{t.dashboard.noMessages}</div>
            ) : msgs.map((m) => {
              const mine = m.user_id === user?.user_id;
              const isAdmin = m.author_role === 'admin';
              return (
                <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? 'bg-orange-500 text-white' : isAdmin ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-900'}`}>
                    <div className={`text-[10px] uppercase tracking-widest mb-0.5 ${mine ? 'text-orange-100' : isAdmin ? 'text-orange-300' : 'text-zinc-500'}`}>
                      {mine ? t.dashboard.you : isAdmin ? t.dashboard.team : m.author_name}
                    </div>
                    <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="px-5 pb-5 pt-2 border-t border-zinc-100">
            <div className="flex gap-2">
              <Textarea
                rows={2}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={t.dashboard.typeMessage}
                className="flex-1 bg-white border-zinc-300 focus-visible:ring-orange-500"
              />
              <Button onClick={sendMsg} disabled={sending || !text.trim()} className="bg-orange-500 hover:bg-orange-600 text-white border-0 self-end h-10">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-2 border-b border-zinc-100 last:border-0">
    <span className="text-zinc-500 text-sm">{label}</span>
    <span className="text-zinc-800 font-medium text-sm">{value || '-'}</span>
  </div>
);

export default OrderDetail;
